'use client';

import { useCallback, useLayoutEffect, useMemo, useRef } from 'react';
import { acquirePhysicalWrite, getPhysicalWrite, releasePhysicalWrite, SCHEDULE_WRITE_SCOPE } from '../hooks/physicalWriteSlot';

export interface WebRegCourse { baseId: string; sectionId: string }
/** A mounted, unchanged review owns its continuation, including across retries. */
export interface WebRegImportReceipt { identity: object; isCurrent: () => boolean }
export interface WebRegSchedulePayload {
  name: string; term_id: string; is_public: true; classes: string[];
  configuration: {
    selectedCourses: { id: string; name: string; allowedSections: string[] }[];
    pinnedClasses: string[]; currentScheduleIndex: number; selectedCourseIds: string[];
    customActivities: unknown[];
  };
}
interface Options {
  ownerId: string | null; routeKey: string; term: string; isOpen: boolean; draft: unknown;
  catalog: readonly { baseId: string; courseName: string }[]; customActivities: readonly unknown[];
  primaryScheduleId: string | null;
  isRouteCurrent: () => boolean;
  createSchedule: (payload: WebRegSchedulePayload) => Promise<{ schedule_id?: string } | null>;
  assignPrimary: (scheduleId: string) => Promise<unknown>;
  refreshDirectory: () => Promise<boolean>;
  navigate: (scheduleId: string) => void;
}
interface Checkpoint {
  context: object; review: object; payloadKey: string;
  scheduleId: string | null; primaryAssigned: boolean; createUnconfirmed: boolean;
}
const clone = <T,>(value: T): T => JSON.parse(JSON.stringify(value));

/** No automatic retry or network idempotency claim. Only confirmed IDs are reusable. */
export function useWebRegScheduleImport(options: Options) {
  const { ownerId, routeKey, term, isOpen, draft } = options;
  const context = useMemo(() => ({ ownerId, routeKey, term, isOpen }), [ownerId, routeKey, term, isOpen]);
  const draftIdentity = useMemo(() => ({ draft }), [draft]);
  const committed = useRef<{ context: typeof context; options: Options; draftIdentity: typeof draftIdentity } | null>(null);
  const lifetime = useRef<symbol | null>(null);
  const checkpoint = useRef<Checkpoint | null>(null);
  useLayoutEffect(() => { committed.current = { context, options, draftIdentity }; });
  useLayoutEffect(() => {
    const epoch = Symbol('webreg-import'); lifetime.current = epoch; checkpoint.current = null;
    return () => { if (lifetime.current === epoch) { lifetime.current = null; checkpoint.current = null; } };
  }, [context]);

  const importSchedule = useCallback(async (courses: WebRegCourse[], name: string, review: WebRegImportReceipt): Promise<boolean> => {
    const current = committed.current; const epoch = lifetime.current;
    if (!epoch || current?.context !== context || current.draftIdentity !== draftIdentity
      || !context.ownerId || !context.isOpen || !current.options.isRouteCurrent() || !review.isCurrent()) return false;
    if (getPhysicalWrite(SCHEDULE_WRITE_SCOPE) !== undefined) return false;
    if (!courses.length || courses.some(course => !course.baseId || !course.sectionId)) throw new Error('Review at least one matched course before importing.');
    const captured = current.options;
    const payload: WebRegSchedulePayload = clone({
      name, term_id: context.term, is_public: true,
      classes: courses.map(course => `${course.baseId}:${course.sectionId}`),
      configuration: {
        selectedCourses: courses.map(course => ({ id: course.baseId,
          name: captured.catalog.find(row => row.baseId === course.baseId)?.courseName || `Course ${course.baseId}`,
          allowedSections: [course.sectionId] })),
        pinnedClasses: [], currentScheduleIndex: 0, selectedCourseIds: courses.map(course => course.baseId),
        customActivities: [...captured.customActivities],
      },
    });
    const payloadKey = JSON.stringify(payload);
    let saved = checkpoint.current;
    if (!saved || saved.context !== context || saved.review !== review.identity || saved.payloadKey !== payloadKey) {
      saved = { context, review: review.identity, payloadKey, scheduleId: null, primaryAssigned: false, createUnconfirmed: false };
      checkpoint.current = saved;
    }
    if (saved.createUnconfirmed) throw new Error('Creation could not be confirmed. Check your schedules before starting another import.');
    const operation = Symbol('webreg-import-write');
    if (!acquirePhysicalWrite(SCHEDULE_WRITE_SCOPE, operation)) return false;
    const isCurrent = () => lifetime.current === epoch && committed.current?.context === context
      && committed.current.draftIdentity === draftIdentity && checkpoint.current === saved
      && getPhysicalWrite(SCHEDULE_WRITE_SCOPE) === operation && review.isCurrent()
      && committed.current.options.isRouteCurrent();
    try {
      if (!isCurrent()) return false;
      if (!saved.scheduleId) {
        saved.createUnconfirmed = true;
        const created = await captured.createSchedule(clone(payload));
        if (!created || typeof created.schedule_id !== 'string' || !created.schedule_id.trim()) throw new Error('Creation could not be confirmed.');
        saved.scheduleId = created.schedule_id; saved.createUnconfirmed = false;
      }
      if (!isCurrent()) return false;
      // A current profile may have gained a primary while create was in flight.
      if (!saved.primaryAssigned && !committed.current!.options.primaryScheduleId) {
        await committed.current!.options.assignPrimary(saved.scheduleId);
        if (!isCurrent()) return false;
        saved.primaryAssigned = true;
      }
      if (!isCurrent()) return false;
      const refreshed = await committed.current!.options.refreshDirectory();
      if (!isCurrent() || !refreshed) return false;
      committed.current!.options.navigate(saved.scheduleId);
      checkpoint.current = null;
      return true;
    } catch (reason) {
      if (!isCurrent()) return false;
      if (saved.createUnconfirmed) throw new Error('Creation could not be confirmed. Check your schedules before starting another import.');
      throw new Error('Your imported schedule was created, but setup could not finish. Retry to finish opening the same schedule.');
    } finally {
      // The physical slot survives owner/modal replacement and unmount until settlement.
      releasePhysicalWrite(SCHEDULE_WRITE_SCOPE, operation);
    }
  }, [context, draftIdentity]);
  return { importSchedule };
}
